import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { StatusCodes } from 'http-status-codes';
import axiosInstanceChat from '@/axios/axiosInstanceChat';
import { ConsentScreenData, CreateThemeData, ScreeningChatQuestion } from '@/types';
import { ConversationStatus } from '@/enums/enums';

const skipStatusesForScreeningQuestions = [
  StatusCodes.NOT_FOUND,
  StatusCodes.FORBIDDEN,
  StatusCodes.CONFLICT,
  StatusCodes.INTERNAL_SERVER_ERROR,
];

const skipStatusesForConsentScreen = [
  StatusCodes.INTERNAL_SERVER_ERROR,
  StatusCodes.NOT_FOUND,
  StatusCodes.BAD_REQUEST,
];

const fetchData = async <T>(endpoint: string, instance = axios): Promise<T> => {
  const { data } = await instance.get(endpoint);
  return data;
};

const handleRetry = (failureCount: number, status: number, skipStatuses: number[]) => {
  if (skipStatuses.includes(status)) return false; // don't retry for these statuses
  return failureCount < 3;
};

export const useThemeQuery = (projectId?: string, isErrorScreen?: boolean) => {
  const url = `${import.meta.env.VITE_API_BASE_URL}/projects${projectId}/theme`;
  return useQuery({
    queryFn: () => fetchData<CreateThemeData>(url),
    queryKey: ['chat_themes_branding', { projectId }],
    enabled: !!projectId && !isErrorScreen,
    retry: (failureCount, error) =>
      handleRetry(failureCount, error?.response?.status, skipStatusesForConsentScreen),
    // this handles the situation where for some reason ther is no theme for the project
    retryOnMount: false,
    // no need to refetch the theme every time
    staleTime: 1000 * 1 * 30, //  30 seconds
  });
};

export const useGetScreeningQuestions = (
  projectId: string,
  enabled: boolean,
  status: ConversationStatus,
  metadata?: Record<string, any>,
) => {
  // Build query string from metadata
  const queryParams = metadata ? new URLSearchParams(metadata).toString() : '';
  const queryString = queryParams ? `?${queryParams}` : '';

  return useQuery({
    queryFn: () =>
      fetchData<ScreeningChatQuestion[]>(
        `chat/${status}/prescreen${projectId}${queryString}`,
        axiosInstanceChat,
      ),
    queryKey: ['prescreen', { projectId, metadata }],
    enabled,
    retry: (failureCount, error) =>
      handleRetry(failureCount, error?.status, skipStatusesForScreeningQuestions),
  });
};

export const useConsentScreen = (projectId?: string) => {
  const url = `${import.meta.env.VITE_API_BASE_URL}/projects/${projectId}/consent_screen`;
  return useQuery({
    queryFn: () => fetchData<ConsentScreenData>(url),
    queryKey: ['consent_screen', { projectId }],
    enabled: !!projectId,
    retry: (failureCount, error) =>
      handleRetry(failureCount, error?.response?.status, skipStatusesForConsentScreen),
  });
};
